/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, type FC, useMemo } from 'react';
import { useEntryPointButtonTrigger } from '@jira/platform__entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';

// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import type { ContentProps } from '@atlaskit/popup/types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useQueryParam } from '@atlassian/react-resource-router';
import type { OnCloseProps, CustomTriggerComponentType } from '../../types.tsx';
import { projectActionMenuEntryPoint } from '../projectActionMenuEntryPoint.entrypoint.tsx';
import { editCustomTemplateQueryParamEntryPoint } from '../edit-custom-template/edit-custom-template-modal/projectActionMenuEditCustomTemplateQueryParam.entrypoint.tsx';
import { MenuContainer } from '../menu-container/index.tsx';
import { Skeleton } from '../skeleton/index.tsx';

export type EntryPointContentProps = OnCloseProps & {
	projectKey: string;
	/**
	 * Numeric project identifier (e.g. `"10001"`) — the stable id used as the
	 * value of the `?editCustomTemplateForProject=<projectId>` deep-link query
	 * param.
	 *
	 * Required when `shouldHandleQueryParamModal` is true (sidebar nav caller):
	 * the receiver self-gates on `projectId === editCustomTemplateForProject`,
	 * and constructs the project ARI from `(cloudId, projectId)` before
	 * preloading `jiraProject(id: $projectAri)`. The numeric id is preferred
	 * over `projectKey` because keys can change when an admin renames the
	 * project, which would break shared/bookmarked deep links.
	 */
	projectId?: string;
	projectName?: string;
	CustomTrigger?: CustomTriggerComponentType;
	onClick?: () => void;
	isCorePremiumUserSeat?: boolean;
	isProjectMenuReorderingEnabled?: boolean;
	additionalMenuItems?: React.ReactNode;
	shouldRenderToParent?: boolean;
	actionMenuId?: string;
	source?: string;
	/**
	 * When true, this instance is eligible to handle opening the edit custom template modal
	 * from the `?editCustomTemplateForProject=<projectId>` query param.
	 *
	 * IMPORTANT: ProjectActionMenu is rendered in TWO locations (sidebar nav and horizontal nav)
	 * AND the sidebar renders one instance per project. Eligibility alone is not enough — the
	 * receiver also self-gates on `projectId === editCustomTemplateForProject` so that exactly
	 * one instance opens the modal regardless of how many sidebar projects are listed.
	 *
	 * Set this to true only in the sidebar nav caller.
	 */
	shouldHandleQueryParamModal?: boolean;
};

export const EntryPointContent: FC<EntryPointContentProps> = ({
	CustomTrigger,
	onClick,
	additionalMenuItems,
	isProjectMenuReorderingEnabled = false,
	isCorePremiumUserSeat = false,
	projectKey,
	projectId,
	shouldRenderToParent,
	actionMenuId,
	projectName,
	source,
	shouldHandleQueryParamModal = false,
}: EntryPointContentProps) => {
	const cloudId = useCloudId();
	// Receiver for the deep-link `?editCustomTemplateForProject=<projectId>`.
	// The query param carries the target project's numeric id so that — even
	// though `shouldHandleQueryParamModal` is true on every sidebar
	// `ProjectActionMenu` (one per project) — only the instance whose
	// `projectId` matches the param value mounts the secondary entry-point
	// container and renders the modal. This prevents N modals from popping
	// when the sidebar has N custom-template projects listed.
	const [editCustomTemplateForProjectParam, setEditCustomTemplateForProject] = useQueryParam(
		'editCustomTemplateForProject',
	);
	const isEditingCustomTemplate =
		// eslint-disable-next-line jira/ff/no-preconditioning
		shouldHandleQueryParamModal &&
		projectId != null &&
		editCustomTemplateForProjectParam === projectId &&
		fg('jira_custom_templates_ux') &&
		expVal('custom_templates_rich_text_details_and_avatar', 'isEnabled', false);

	const entryPointParams = useMemo(() => ({ cloudId, projectKey }), [cloudId, projectKey]);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		projectActionMenuEntryPoint,
		entryPointParams,
	);
	const buttonTriggerRef = useEntryPointButtonTrigger(entryPointActions);

	// Build the project ARI inline (the established pattern across the codebase
	// — see e.g. `OpsGenieSettingsUi`, `DriverField`) so the deep-link URL
	// itself stays short and human-readable while Relay still receives the
	// fully-qualified `id` argument it requires for `jiraProject(id:)`.
	const editTemplateEntryPointParams = useMemo(
		() => ({ projectAri: projectId ? `ari:cloud:jira:${cloudId}:project/${projectId}` : '' }),
		[cloudId, projectId],
	);

	const {
		entryPointActions: editTemplateModalActions,
		entryPointReferenceSubject: editTemplateModalReferenceSubject,
	} = useEntryPoint(editCustomTemplateQueryParamEntryPoint, editTemplateEntryPointParams);

	if (isEditingCustomTemplate) {
		editTemplateModalActions.load();
	}

	const clearEditCustomTemplateParam = useCallback(() => {
		setEditCustomTemplateForProject(undefined);
	}, [setEditCustomTemplateForProject]);

	const content = useCallback(
		(contentProps: ContentProps) => (
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="project-actions-menu-popup-content"
				packageName="jira-navigation-apps-project-action-menu"
				// oxlint-disable-next-line react-hooks/rules-of-hooks
				runtimeProps={useMemo(
					() => ({
						isCorePremiumUserSeat,
						additionalMenuItems,
						isProjectMenuReorderingEnabled,
						contentProps,
						source,
					}),
					[contentProps],
				)}
				fallback={<Skeleton />}
			/>
		),
		[
			entryPointReferenceSubject,
			additionalMenuItems,
			isCorePremiumUserSeat,
			isProjectMenuReorderingEnabled,
			source,
		],
	);

	const queryParamRuntimeProps = useMemo(
		() => ({ onClose: clearEditCustomTemplateParam }),
		[clearEditCustomTemplateParam],
	);

	return (
		<>
			{isEditingCustomTemplate && (
				<JiraEntryPointContainer
					entryPointReferenceSubject={editTemplateModalReferenceSubject}
					id="project-actions-menu-query-param-modal"
					packageName="jira-navigation-apps-project-action-menu"
					runtimeProps={queryParamRuntimeProps}
					fallback={null}
				/>
			)}
			<MenuContainer
				projectName={projectName}
				content={content}
				CustomTrigger={CustomTrigger}
				onClick={onClick}
				buttonTriggerRef={buttonTriggerRef}
				shouldRenderToParent={shouldRenderToParent}
				actionMenuId={actionMenuId}
			/>
		</>
	);
};
